"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "@/lib/store";

type Node = {
  id: string;
  kind: "doc" | "issue" | "project" | "cycle" | "note";
  label: string;
  color: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  entityId: string;
};

type Edge = {
  from: string;
  to: string;
  kind: "parent" | "wikilink" | "mention" | "project" | "cycle" | "label";
};

/**
 * Workspace-wide force-directed graph. Renders every entity (docs, issues,
 * projects, cycles, notes) as a node; edges come from:
 *   - doc.parent_id → parent doc
 *   - [[wikilinks]] in doc.content
 *   - @MENTIONS in doc/issue/note bodies → issues
 *   - issue.project_id / issue.cycle_id
 *   - shared labels (tag-like clustering via issue.labelIds) — only rendered
 *     between issues sharing the same label to avoid hairball.
 *
 * We implement a minimal Barnes-Hut-free velocity-Verlet simulation on
 * Canvas — no extra deps. Good enough up to a few hundred nodes.
 */
export function GraphView() {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const docs = useStore((s) => s.docs);
  const issues = useStore((s) => s.issues);
  const projects = useStore((s) => s.projects);
  const cycles = useStore((s) => s.cycles);
  const notes = useStore((s) => s.notes);
  const selectDoc = useStore((s) => s.selectDoc);
  const selectIssue = useStore((s) => s.selectIssue);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  // Build nodes + edges from store
  const { nodes, edges } = useMemo(() => {
    const wsDocs = docs.filter((d) => d.workspaceId === activeWorkspaceId);
    const wsIssues = issues.filter((i) => i.workspaceId === activeWorkspaceId);
    const wsProjects = projects.filter((p) => p.workspaceId === activeWorkspaceId);
    const wsCycles = cycles; // per-team; we scope later via teamId membership
    const wsNotes = notes.filter((n) => n.workspaceId === activeWorkspaceId);

    const idFor = (kind: Node["kind"], entityId: string) => `${kind}:${entityId}`;

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    for (const d of wsDocs) {
      nodes.push({
        id: idFor("doc", d.id),
        kind: "doc",
        label: d.title,
        color: "var(--accent)",
        x: 0, y: 0, vx: 0, vy: 0,
        radius: 7 + Math.min(10, Math.log(Math.max(2, d.content.length)) - 1),
        entityId: d.id,
      });
    }
    for (const i of wsIssues) {
      nodes.push({
        id: idFor("issue", i.id),
        kind: "issue",
        label: `${i.identifier} · ${i.title}`,
        color:
          i.status === "done"
            ? "var(--green)"
            : i.status === "in_progress"
            ? "var(--blue)"
            : i.status === "canceled"
            ? "var(--fg-muted)"
            : "var(--fg-tertiary)",
        x: 0, y: 0, vx: 0, vy: 0,
        radius: 5,
        entityId: i.id,
      });
    }
    for (const p of wsProjects) {
      nodes.push({
        id: idFor("project", p.id),
        kind: "project",
        label: p.name,
        color: p.color || "var(--purple)",
        x: 0, y: 0, vx: 0, vy: 0,
        radius: 10,
        entityId: p.id,
      });
    }
    for (const c of wsCycles) {
      nodes.push({
        id: idFor("cycle", c.id),
        kind: "cycle",
        label: c.name,
        color: "var(--orange)",
        x: 0, y: 0, vx: 0, vy: 0,
        radius: 6,
        entityId: c.id,
      });
    }
    for (const n of wsNotes) {
      nodes.push({
        id: idFor("note", n.id),
        kind: "note",
        label: n.body.slice(0, 40),
        color: "var(--fg-tertiary)",
        x: 0, y: 0, vx: 0, vy: 0,
        radius: 4,
        entityId: n.id,
      });
    }

    // doc.parent_id
    for (const d of wsDocs) {
      if (d.parentId) edges.push({ from: idFor("doc", d.id), to: idFor("doc", d.parentId), kind: "parent" });
    }
    // [[wikilinks]] and @MENTIONS inside doc content
    const titleToId = new Map<string, string>();
    for (const d of wsDocs) titleToId.set(d.title.toLowerCase(), d.id);
    const identToId = new Map<string, string>();
    for (const i of wsIssues) identToId.set(i.identifier.toLowerCase(), i.id);

    const sourceBodies: { id: string; body: string }[] = [
      ...wsDocs.map((d) => ({ id: idFor("doc", d.id), body: d.content })),
      ...wsIssues.map((i) => ({ id: idFor("issue", i.id), body: i.description || "" })),
      ...wsNotes.map((n) => ({ id: idFor("note", n.id), body: n.body })),
    ];
    const wikiRe = /\[\[([^\]]+?)\]\]/g;
    const mentRe = /(^|[\s(])@([A-Z]{2,5}-\d+)/g;
    for (const s of sourceBodies) {
      let m: RegExpExecArray | null;
      wikiRe.lastIndex = 0;
      while ((m = wikiRe.exec(s.body)) !== null) {
        const target = titleToId.get(m[1].trim().toLowerCase());
        if (target) edges.push({ from: s.id, to: idFor("doc", target), kind: "wikilink" });
      }
      mentRe.lastIndex = 0;
      while ((m = mentRe.exec(s.body)) !== null) {
        const target = identToId.get(m[2].toLowerCase());
        if (target) edges.push({ from: s.id, to: idFor("issue", target), kind: "mention" });
      }
    }
    // issue.project_id / issue.cycle_id
    for (const i of wsIssues) {
      if (i.projectId) edges.push({ from: idFor("issue", i.id), to: idFor("project", i.projectId), kind: "project" });
      if (i.cycleId) edges.push({ from: idFor("issue", i.id), to: idFor("cycle", i.cycleId), kind: "cycle" });
    }

    // Initial layout: polar distribution by kind
    const kinds = ["project", "doc", "issue", "cycle", "note"] as const;
    const byKind = new Map<string, Node[]>();
    for (const k of kinds) byKind.set(k, []);
    for (const n of nodes) byKind.get(n.kind)!.push(n);
    let angle = 0;
    const cx = 500, cy = 400;
    for (const k of kinds) {
      const arr = byKind.get(k)!;
      const ring = 80 + kinds.indexOf(k) * 120;
      const step = (Math.PI * 2) / Math.max(1, arr.length);
      for (const n of arr) {
        n.x = cx + Math.cos(angle) * ring;
        n.y = cy + Math.sin(angle) * ring;
        angle += step;
      }
    }

    return { nodes, edges };
  }, [docs, issues, projects, cycles, notes, activeWorkspaceId]);

  // Force simulation — runs for a limited number of ticks then freezes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const idMap = new Map<string, Node>();
    for (const n of nodes) idMap.set(n.id, n);

    const W = canvas.width;
    const H = canvas.height;
    let ticks = 0;
    const MAX_TICKS = 400;

    // Pan / zoom
    let panX = 0, panY = 0, zoom = 1;

    function step() {
      if (!ctx) return;
      const REPULSE = 5500;
      const CENTER_PULL = 0.001;
      const LINK_LEN = 110;
      const LINK_K = 0.03;
      const DAMP = 0.82;

      // Repulsion
      for (let a = 0; a < nodes.length; a++) {
        for (let b = a + 1; b < nodes.length; b++) {
          const na = nodes[a];
          const nb = nodes[b];
          const dx = na.x - nb.x;
          const dy = na.y - nb.y;
          const dist2 = dx * dx + dy * dy + 0.01;
          const force = REPULSE / dist2;
          const dist = Math.sqrt(dist2);
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          na.vx += fx;
          na.vy += fy;
          nb.vx -= fx;
          nb.vy -= fy;
        }
      }
      // Spring along edges
      for (const e of edges) {
        const a = idMap.get(e.from);
        const b = idMap.get(e.to);
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) + 0.01;
        const disp = dist - LINK_LEN;
        const fx = (dx / dist) * disp * LINK_K;
        const fy = (dy / dist) * disp * LINK_K;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      }
      // Center attraction
      for (const n of nodes) {
        n.vx += (W / 2 - n.x) * CENTER_PULL;
        n.vy += (H / 2 - n.y) * CENTER_PULL;
        n.vx *= DAMP;
        n.vy *= DAMP;
        n.x += n.vx;
        n.y += n.vy;
      }
      draw();
      ticks++;
      if (ticks < MAX_TICKS) rafId = requestAnimationFrame(step);
    }

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.translate(panX, panY);
      ctx.scale(zoom, zoom);
      // edges
      ctx.lineWidth = 1;
      for (const e of edges) {
        const a = idMap.get(e.from);
        const b = idMap.get(e.to);
        if (!a || !b) continue;
        ctx.strokeStyle =
          e.kind === "parent"
            ? "rgba(200,200,220,0.35)"
            : e.kind === "wikilink"
            ? "rgba(120,170,255,0.55)"
            : e.kind === "mention"
            ? "rgba(235,90,90,0.55)"
            : "rgba(170,170,190,0.25)";
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      // nodes
      for (const n of nodes) {
        ctx.fillStyle = cssVarTo(n.color);
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
        ctx.fill();
        if (hoverId === n.id) {
          ctx.strokeStyle = "white";
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.fillStyle = "white";
          ctx.font = "12px -apple-system, system-ui, sans-serif";
          const txt = n.label.length > 80 ? n.label.slice(0, 80) + "…" : n.label;
          ctx.fillText(txt, n.x + n.radius + 6, n.y + 4);
        }
      }
      ctx.restore();
    }

    function hitTest(mx: number, my: number): Node | null {
      const x = (mx - panX) / zoom;
      const y = (my - panY) / zoom;
      for (let i = nodes.length - 1; i >= 0; i--) {
        const n = nodes[i];
        const dx = x - n.x;
        const dy = y - n.y;
        if (dx * dx + dy * dy <= (n.radius + 3) * (n.radius + 3)) return n;
      }
      return null;
    }

    let rafId = requestAnimationFrame(step);

    const onMove = (ev: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const n = hitTest(ev.clientX - rect.left, ev.clientY - rect.top);
      setHoverId(n?.id ?? null);
      canvas.style.cursor = n ? "pointer" : "default";
      draw();
    };
    const onClick = (ev: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const n = hitTest(ev.clientX - rect.left, ev.clientY - rect.top);
      if (!n) return;
      if (n.kind === "doc") selectDoc(n.entityId);
      else if (n.kind === "issue") selectIssue(n.entityId);
    };
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mx = ev.clientX - rect.left;
      const my = ev.clientY - rect.top;
      const oldZoom = zoom;
      zoom *= ev.deltaY < 0 ? 1.1 : 0.9;
      zoom = Math.max(0.3, Math.min(3, zoom));
      panX = mx - (mx - panX) * (zoom / oldZoom);
      panY = my - (my - panY) * (zoom / oldZoom);
      draw();
    };
    let dragging = false;
    let lastX = 0, lastY = 0;
    const onDown = (ev: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (hitTest(ev.clientX - rect.left, ev.clientY - rect.top)) return;
      dragging = true;
      lastX = ev.clientX;
      lastY = ev.clientY;
    };
    const onUp = () => { dragging = false; };
    const onDrag = (ev: MouseEvent) => {
      if (!dragging) return;
      panX += ev.clientX - lastX;
      panY += ev.clientY - lastY;
      lastX = ev.clientX;
      lastY = ev.clientY;
      draw();
    };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("click", onClick);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("mousemove", onDrag);
    return () => {
      cancelAnimationFrame(rafId);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("click", onClick);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("mousemove", onDrag);
    };
  }, [nodes, edges, hoverId, selectDoc, selectIssue]);

  // Resize canvas to fill parent
  useEffect(() => {
    function resize() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      setDims({ w: rect.width, h: rect.height });
    }
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[var(--bg)]">
      <div className="px-5 h-11 shrink-0 border-b border-[var(--border)] flex items-center gap-4 text-[12px] text-[var(--fg-tertiary)]">
        <span className="font-semibold text-[var(--fg)]">Graph</span>
        <Legend />
        <span className="ml-auto text-[11px] text-[var(--fg-muted)]">
          {nodes.length} nodes · {edges.length} edges — scroll to zoom, drag to pan
        </span>
      </div>
      <div className="flex-1 relative overflow-hidden">
        <canvas ref={canvasRef} className="absolute inset-0" />
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-[12.5px] text-[var(--fg-muted)]">
            No entities in this workspace yet — create docs or tasks to see the graph.
          </div>
        )}
      </div>
    </div>
  );
}

function Legend() {
  const items = [
    { label: "Doc", color: "var(--accent)" },
    { label: "Task", color: "var(--blue)" },
    { label: "Project", color: "var(--purple)" },
    { label: "Cycle", color: "var(--orange)" },
    { label: "Note", color: "var(--fg-tertiary)" },
  ];
  return (
    <div className="flex items-center gap-3">
      {items.map((i) => (
        <span key={i.label} className="flex items-center gap-1.5 text-[11px]">
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: i.color }}
          />
          {i.label}
        </span>
      ))}
    </div>
  );
}

function cssVarTo(v: string): string {
  if (!v.startsWith("var(")) return v;
  if (typeof window === "undefined") return "#888";
  const name = v.slice(4, -1).trim();
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "#888";
}
